import React, { useMemo } from 'react';
import { Button, Flex, Layout, Input, Grid, Skeleton } from 'antd';
import { styled, css } from 'styled-components';
import {
  GiftOutlined,
  BellOutlined,
  MessageOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../shared/hooks/use-app-selector';
import { UserProfile } from './UserProfile';
import { AppointmentNotifications } from './AppointmentNotifications';
import { openCelloWidget } from '../../utils';
import {
  BEAMER_ID,
  CELLO_PRODUCT_ID,
  NOTIFICATION_ELEMENT_ID,
} from '../../constants';
import EVENTS from '../../constants/event-bus-events';
import { t } from '../../utils/i18n';
import { navigateTo } from '../../actions';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { getAppSettings, isWsbMerchant } from '../../selectors';
import { getHelpCenterLink } from '../../shared/utils';
import { UserActionNotifications } from './user-action-notifications';
import { HeaderBrandLogo } from './brand/HeaderBrandLogo';
import { useMobileOverlay } from '../../hooks/use-mobile-overlay';
import { touchTargetStyles } from './touch-target';

const { Header: AntdHeader } = Layout;
const { useBreakpoint } = Grid;

const StyledHeader = styled(AntdHeader)<{ $collapsed?: boolean }>`
  background: ${({ theme }) => theme.antd.colorWhite};
  /* The manifest declares display: standalone, so installed on a phone this row
     is the one under the status bar and the notch. Grow the box by the inset and
     pad it away, leaving the content box the 64px antd expects. */
  height: calc(64px + env(safe-area-inset-top, 0px));
  padding: env(safe-area-inset-top, 0px)
    max(1.5rem, env(safe-area-inset-right, 0px)) 0
    max(1.5rem, env(safe-area-inset-left, 0px));
  border-bottom: 1px solid ${({ theme }) => theme.antd.colorPrimary};
  width: 100%;
  margin-left: auto;
  flex: 0 0 auto;
  margin-bottom: 1px;
  line-height: normal;
  transition: width 200ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const StyledIconButton = styled(Button)`
  flex-shrink: 0;
  ${touchTargetStyles}
`;

// The header's desktop glyphs are 16px, which looks adrift once the button grows
// to a 44px touch target — and exaggerates the gap to whatever sits next to it.
const MOBILE_ICON_STYLE = { fontSize: '20px' };

const StyledHeaderIcon = styled.span`
  font-size: 2rem;
  cursor: pointer;
  outline: none;
  color: ${({ theme }) => theme.antd.colorTextSecondary};

  &:hover {
    color: ${({ theme }) => theme.antd.colorPrimary};
  }
`;

const StyledLink = styled.a`
  outline: none;
  line-height: normal;
  color: ${({ theme }) => theme.antd.colorTextSecondary};

  &:hover {
    color: ${({ theme }) => theme.antd.colorPrimary};
  }
`;

const StyledSearchWrapper = styled.div<{
  $collapsed?: boolean;
  $fullWidth?: boolean;
}>`
  position: relative;
  width: 28rem;

  @media (max-width: 768px) {
    width: 26rem;
  }

  @media (max-width: 481px) {
    width: 22rem;
  }

  /* Below md the field is either the whole bar or nothing. It stays in the DOM
     either way: the as-SearchWrapper-searchInput node is the mount point the
     Search micro-frontend renders into, and remounting it would drop that app. */
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      flex: 1 1 auto;
      width: auto;
      min-width: 0;
    `}

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      display: none;
    `}
`;
const StyledDynamicSearchInput = styled.div`
  height: 28px;
  position: absolute;
  top: 3px;
  /* The Search micro-frontend mounts a white, borderless input in here, over the
     antd input that frames it. At left: 0 that paints across the 1px left
     border, leaving only its rounded corners — so start inside the border. */
  left: 1px;
  z-index: 1;
`;

export function Header({
  sideMenuCollapsed = false,
  onSideMenuCollapseChange,
  onMobileMenuOpen,
}: {
  sideMenuCollapsed?: boolean;
  onSideMenuCollapseChange?: (collapsed: boolean) => void;
  /**
   * Below `md` the side menu lives in a full-width drawer instead of the inline
   * rail, so the header button opens that drawer rather than toggling collapse.
   */
  onMobileMenuOpen?: () => void;
}) {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { trackMethod, eventBus } = useGlobals();

  // Below `md` a 352px search field took over half the bar and still truncated
  // its own placeholder, leaving no room for the brand. Collapse it to a button
  // that hands the whole row over when it is actually wanted.
  const {
    isMobile,
    isOpen: isMobileSearchOpen,
    open: openMobileSearch,
    close: closeMobileSearch,
  } = useMobileOverlay();

  const shouldShowProductNotification = Boolean(BEAMER_ID);

  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const isWsb = useAppSelector(isWsbMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const sessionPermissions = useAppSelector(
    (state) => state.sessionPermissions
  );

  const isDataLoading = useMemo(
    () => Object.values(sessionPermissions).some((value) => value === null),
    [sessionPermissions]
  );

  const {
    company,
    whiteLabelConfiguration: { schemaless: whiteLabelSchemaless },
  } = useAppSelector(getAppSettings);

  const locale = currentAccount?.locale || 'en';
  const homeHelpCenterLink = getHelpCenterLink({
    key: 'home',
    config: whiteLabelSchemaless,
    locale,
    companyName: company,
  });

  const handleOnCollapseButtonClick = () => {
    if (onSideMenuCollapseChange) {
      onSideMenuCollapseChange(!sideMenuCollapsed);
    }
    if (!sideMenuCollapsed) {
      eventBus.emit(EVENTS.SIDE_MENU_COLLAPSE);
    } else {
      eventBus.emit(EVENTS.SIDE_MENU_EXPAND);
    }
  };

  const handleOnSearchInputWrapperClick = () => {
    /*
      Note: This dispatch loads the Search micro-frontend,
      mounts the SearchBar component App into the .as-SearchWrapper-searchInput element,
      and then focuses the input field rendered by the SearchBar component.
    */
    dispatch(navigateTo('#/search'));
  };

  const handleOnMobileSearchOpen = () => {
    openMobileSearch();
    handleOnSearchInputWrapperClick();
  };

  const handleOnReferralButtonClick = () => {
    trackMethod('top_menu_item_clicked', {
      platform: 'booking',
      location: 'top_menu_button',
      button_type: 'referral_earn',
    });
    openCelloWidget();
  };

  const handleOnMessengerIconClick = () => {
    trackMethod('top_menu_item_clicked', {
      platform: 'booking',
      location: 'top_menu_button',
      button_type: 'messenger',
    });
  };

  const handleOnProductNotificationIconClick = () => {
    trackMethod('top_menu_item_clicked', {
      platform: 'booking',
      location: 'top_menu_button',
      button_type: 'product_notifications',
    });
  };

  if (isWsb) {
    return (
      <>
        {screens.md && <UserActionNotifications />}
        <StyledHeader>
          <Flex
            justify="space-between"
            align="center"
            style={{ height: '100%' }}
          >
            <Flex align="center" gap="16px">
              <StyledSearchWrapper onClick={handleOnSearchInputWrapperClick}>
                <Input.Search
                  allowClear
                  placeholder="Find customers or appointments"
                  style={{ width: screens.md ? 280 : '100%' }}
                />
                {
                  // TODO: Improve Search bar later
                  <StyledDynamicSearchInput
                    className="as-SearchWrapper-searchInput"
                    style={{
                      width: `calc(${screens.md ? 280 : '100%'} - 30px)`,
                    }}
                  />
                }
              </StyledSearchWrapper>
            </Flex>
            <Flex align="center" gap="16px">
              {screens.md && (
                <Flex gap="16px">
                  <AppointmentNotifications />
                </Flex>
              )}
            </Flex>
          </Flex>
        </StyledHeader>
      </>
    );
  }

  return (
    <>
      {screens.md && <UserActionNotifications />}
      <StyledHeader>
        <Flex justify="space-between" align="center" style={{ height: '100%' }}>
          <Flex
            align="center"
            gap="16px"
            style={{ flex: '1 1 auto', minWidth: 0 }}
          >
            {screens.md ? (
              <StyledIconButton
                type="text"
                aria-label={
                  sideMenuCollapsed
                    ? t('side-menu.expand-menu')
                    : t('side-menu.collapse-menu')
                }
                icon={
                  sideMenuCollapsed ? (
                    <MenuUnfoldOutlined />
                  ) : (
                    <MenuFoldOutlined />
                  )
                }
                onClick={handleOnCollapseButtonClick}
                style={{ fontSize: '16px' }}
              />
            ) : isMobileSearchOpen ? (
              <StyledIconButton
                type="text"
                aria-label={t('header.close-search')}
                icon={<ArrowLeftOutlined />}
                onClick={closeMobileSearch}
                style={MOBILE_ICON_STYLE}
              />
            ) : (
              // Menu button and brand are one cluster, so they sit tighter than
              // the 16px that separates the bar's separate groups.
              <Flex align="center" gap="4px">
                {onMobileMenuOpen && (
                  <StyledIconButton
                    type="text"
                    aria-label={t('side-menu.open-menu')}
                    icon={<MenuUnfoldOutlined />}
                    onClick={onMobileMenuOpen}
                    style={MOBILE_ICON_STYLE}
                  />
                )}
                {/* The sider carries the brand from `md` up; below it there is
                    nothing else on screen that does. */}
                <HeaderBrandLogo />
              </Flex>
            )}
            <StyledSearchWrapper
              $collapsed={isMobile && !isMobileSearchOpen}
              $fullWidth={isMobile && isMobileSearchOpen}
              onClick={handleOnSearchInputWrapperClick}
            >
              <Input.Search
                allowClear
                placeholder="Find customers or appointments"
                style={{ width: screens.md ? 280 : '100%' }}
              />
              {
                // TODO: Improve Search bar later
                <StyledDynamicSearchInput
                  className="as-SearchWrapper-searchInput"
                  style={{ width: `calc(${screens.md ? 280 : '100%'} - 31px)` }}
                />
              }
            </StyledSearchWrapper>
          </Flex>
          <Flex
            align="center"
            gap="16px"
            style={{
              flex: '0 0 auto',
              // The field owns the row while it is open.
              display: isMobile && isMobileSearchOpen ? 'none' : undefined,
            }}
          >
            {screens.lg && CELLO_PRODUCT_ID && (
              <Flex
                gap="16px"
                style={{ position: 'relative' }}
                className="cello-launcher-announcement"
              >
                <Button
                  size="middle"
                  color="primary"
                  variant="outlined"
                  icon={<GiftOutlined />}
                  onClick={handleOnReferralButtonClick}
                >
                  {t('button.referral.title')}
                  <div className="cello-launcher" />
                </Button>
              </Flex>
            )}
            {screens.md && (
              <Flex gap="16px">
                <AppointmentNotifications />
                {currentMerchant.featureToggles.messaging && (
                  <StyledLink
                    data-route
                    href="#/messenger/conversations"
                    onClick={handleOnMessengerIconClick}
                  >
                    <StyledHeaderIcon>
                      <MessageOutlined />
                    </StyledHeaderIcon>
                  </StyledLink>
                )}
                {shouldShowProductNotification && (
                  <StyledHeaderIcon
                    id={NOTIFICATION_ELEMENT_ID}
                    onClick={handleOnProductNotificationIconClick}
                  >
                    <BellOutlined />
                  </StyledHeaderIcon>
                )}
              </Flex>
            )}
            {isMobile && (
              <StyledIconButton
                type="text"
                aria-label={t('header.open-search')}
                icon={<SearchOutlined />}
                onClick={handleOnMobileSearchOpen}
                style={MOBILE_ICON_STYLE}
              />
            )}
            {isDataLoading ? (
              <Skeleton.Input
                active
                size="small"
                style={{ height: 44, width: 20 }}
              />
            ) : (
              <UserProfile homeHelpCenterLink={homeHelpCenterLink} />
            )}
          </Flex>
        </Flex>
      </StyledHeader>
    </>
  );
}
